import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const normalizeString = (str) => {
  return str
    ? str
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    : '';
};

const toUpperTr = (value) => (value || '').toLocaleUpperCase('tr-TR');

const formatPhoneNumber = (value) => {
  const raw = (value || '').replace(/[^\d]/g, '');
  let local = raw;
  if (local.startsWith('90')) {
    local = local.slice(2);
  }
  local = local.slice(0, 10);

  const p1 = local.slice(0, 3);
  const p2 = local.slice(3, 6);
  const p3 = local.slice(6, 8);
  const p4 = local.slice(8, 10);

  let formatted = '+90';
  if (p1) formatted += ` ${p1}`;
  if (p2) formatted += ` ${p2}`;
  if (p3) formatted += ` ${p3}`;
  if (p4) formatted += ` ${p4}`;
  if (!p1) formatted += ' ';
  return formatted;
};

function QuickCompanyAddModal({ open, onClose, sube, onSuccess }) {
  const [firmaIsmi, setFirmaIsmi] = useState('');
  const [telefon, setTelefon] = useState('+90 ');
  const [vergiNo, setVergiNo] = useState('');
  const [vergiDairesi, setVergiDairesi] = useState('');
  const [errors, setErrors] = useState({});
  const [hata, setHata] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);

  useEffect(() => {
    if (!open) {
      setFirmaIsmi('');
      setTelefon('+90 ');
      setVergiNo('');
      setVergiDairesi('');
      setErrors({});
      setHata('');
      setKaydediliyor(false);
    }
  }, [open]);

  const validateForm = async () => {
    const newErrors = {};
    const trimmedFirma = firmaIsmi.trim();

    if (!trimmedFirma) {
      newErrors.firmaIsmi = 'Firma Adı / Ünvanı zorunlu';
    }

    if (telefon && telefon.trim() !== '+90' && !/^\+90\s\d{3}\s\d{3}\s\d{2}\s\d{2}$/.test(telefon)) {
      newErrors.telefon = 'Telefon formatı +90 5xx xxx xx xx olmalı';
    }

    if (vergiNo && !/^\d{10,}$/.test(vergiNo)) {
      newErrors.vergiNo = 'Geçerli bir vergi numarası girin';
    }

    if (trimmedFirma) {
      const normalizedFirmaId = normalizeString(trimmedFirma);
      if (!normalizedFirmaId) {
        newErrors.firmaIsmi = 'Geçerli bir firma adı girin';
      } else {
        const firmaRef = doc(db, 'subeler', sube, 'firmalar', normalizedFirmaId);
        const firmaSnap = await getDoc(firmaRef);
        if (firmaSnap.exists()) {
          newErrors.firmaIsmi = 'Bu firma ismi zaten kayıtlı';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setHata('');
    if (!(await validateForm())) return;

    setKaydediliyor(true);
    try {
      const trimmedFirma = firmaIsmi.trim();
      const normalizedFirmaId = normalizeString(trimmedFirma);
      const firmaIletisimNo = telefon.trim() === '+90' ? null : telefon.trim() || null;

      const firmaRef = doc(db, 'subeler', sube, 'firmalar', normalizedFirmaId);
      await setDoc(firmaRef, {
        firmaIsmi: trimmedFirma,
        yetkili: null,
        vergiNo: vergiNo || null,
        vergiDairesi: vergiDairesi.trim() || null,
        firmaAdresi: null,
        firmaIletisimNo,
        yetkiliIletisimNo: null,
        bilgiler: null,
        timestamp: new Date(),
        sube,
      });

      const newFirma = {
        id: normalizedFirmaId,
        firmaIsmi: trimmedFirma,
        ad: trimmedFirma,
        vergiNo: vergiNo || null,
        vergiDairesi: vergiDairesi.trim() || null,
        firmaIletisimNo,
      };

      onSuccess(newFirma);
    } catch (err) {
      setHata(`Firma eklenemedi: ${err.message}`);
      console.error('Firestore Hatası:', err);
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          m: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontSize: { xs: '18px', sm: '20px' },
          fontWeight: 700,
          color: '#102a43',
        }}
      >
        Yeni Firma Ekle
        <IconButton onClick={onClose} size="small" aria-label="Kapat">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Firma Adı / Ünvanı"
            required
            fullWidth
            size="small"
            value={firmaIsmi}
            onChange={(e) => setFirmaIsmi(toUpperTr(e.target.value))}
            error={!!errors.firmaIsmi}
            helperText={errors.firmaIsmi}
            autoFocus
            inputProps={{ style: { fontSize: '16px' } }}
          />
          <TextField
            label="Telefon"
            fullWidth
            size="small"
            value={telefon}
            onChange={(e) => setTelefon(formatPhoneNumber(e.target.value))}
            placeholder="+90 5xx xxx xx xx"
            error={!!errors.telefon}
            helperText={errors.telefon}
            inputProps={{ style: { fontSize: '16px' } }}
          />
          <TextField
            label="Vergi No"
            fullWidth
            size="small"
            value={vergiNo}
            onChange={(e) => setVergiNo((e.target.value || '').replace(/\D/g, '').slice(0, 11))}
            placeholder="Vergi No"
            error={!!errors.vergiNo}
            helperText={errors.vergiNo}
            inputProps={{
              style: { fontSize: '16px' },
              inputMode: 'numeric',
            }}
          />
          <TextField
            label="Vergi Dairesi"
            fullWidth
            size="small"
            value={vergiDairesi}
            onChange={(e) => setVergiDairesi(toUpperTr(e.target.value))}
            placeholder="Vergi Dairesi"
            inputProps={{ style: { fontSize: '16px' } }}
          />
          {hata && (
            <Typography
              variant="body2"
              sx={{
                color: '#b91c1c',
                bgcolor: '#fee2e2',
                border: '1px solid #fecaca',
                p: 1,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              {hata}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={kaydediliyor}>
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={kaydediliyor}
          sx={{
            bgcolor: '#a8671d',
            '&:hover': { bgcolor: '#955d18' },
          }}
        >
          {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickCompanyAddModal;
